import React, { useEffect, useState } from "react";
import { Check, Crown, Network as NetIcon, MessageCircle, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TELEGRAM = "https://t.me/raeinexchangesupport";

// VIP is free (promo) until this date.
const PROMO_END = new Date("2026-09-01T00:00:00Z");
const vipFree = Date.now() < PROMO_END.getTime();

const PLAN_META = {
  vip_monthly: {
    icon: Crown,
    accent: "amber",
    period: "mese",
    tagline: "Tutti i segnali premium + Raeinex Network incluso.",
    benefits: [
      "Accesso a TUTTI i segnali premium",
      "Segnali su XAUUSD, Bitcoin, Crypto e Forex",
      "Entry, Stop Loss e Take Profit completi",
      "Notifiche push in tempo reale",
      "Raeinex Network incluso: commissioni NP, bonus NB e Wallet",
    ],
  },
  investor_yearly: {
    icon: NetIcon,
    accent: "blue",
    period: "anno",
    tagline: "Il VIP per un anno intero al prezzo di 9 mesi.",
    benefits: [
      "Tutto ciò che include il VIP, per 12 mesi",
      "Paghi come 9 mesi: 3 mesi in regalo",
      "Raeinex Network incluso con NP potenziati",
      "Bonus NB e Wallet dedicato",
      "Link di invito personale",
    ],
  },
};

const ACCENT = {
  amber: { border: "border-amber-500/30", glow: "bg-amber-500/10", btn: "bg-amber-500 text-black hover:bg-amber-400", chip: "bg-amber-500/15 text-amber-400", icon: "text-amber-400" },
  blue: { border: "border-blue-500/30", glow: "bg-blue-500/10", btn: "bg-blue-500 text-white hover:bg-blue-400", chip: "bg-blue-500/15 text-blue-400", icon: "text-blue-400" },
};

export default function Pricing() {
  const { user, checkAuth } = useAuth();
  const [plans, setPlans] = useState([]);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const cancel = async () => {
    if (!window.confirm("Vuoi annullare il tuo abbonamento? Perderai subito l'accesso ai segnali premium e al network.")) return;
    try {
      await api.post("/payments/cancel-subscription");
      toast.success("Abbonamento annullato.");
      checkAuth && checkAuth();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="text-center mb-12">
        <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20 mb-4">
          <Crown size={12} /> ABBONAMENTI
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Scegli il tuo piano</h1>
        <p className="text-zinc-400 mt-3 max-w-lg mx-auto">Segnali premium in tempo reale e accesso al Raeinex Network, inclusi in ogni piano.</p>
      </div>

      {vipFree && (
        <div data-testid="vip-promo-banner" className="mb-8 p-5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-center">
          <div className="flex items-center justify-center gap-2 text-emerald-400 font-semibold">
            <Gift size={18} /> Promo: il piano VIP è a 0€ fino al 31 agosto 2026
          </div>
          <a data-testid="vip-promo-cta" href={TELEGRAM} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-3 px-5 py-2.5 rounded-md bg-emerald-500 text-black text-sm font-semibold hover:bg-emerald-400 transition-colors">
            <MessageCircle size={15} /> Scrivici per diventare VIP ora
          </a>
        </div>
      )}

      {user?.is_premium && (
        <div data-testid="already-premium" className="mb-8 p-4 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-center text-emerald-400 text-sm">
          {isAdmin ? "Account admin: accesso completo permanente." : (
            <>Abbonamento attivo! {user.premium_until && `Valido fino al ${new Date(user.premium_until).toLocaleDateString("it-IT")}.`}</>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {plans.map((p) => {
          const meta = PLAN_META[p.id] || PLAN_META.vip_monthly;
          const acc = ACCENT[meta.accent];
          const Icon = meta.icon;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`} className={`bg-[#141414] border ${acc.border} rounded-md p-8 relative overflow-hidden flex flex-col`}>
              <div className={`absolute top-0 right-0 w-40 h-40 ${acc.glow} blur-3xl rounded-full`} />
              <div className="relative flex flex-col h-full">
                <div className="flex items-center gap-2">
                  <Icon size={18} className={acc.icon} />
                  <h2 className="text-xl font-semibold">{p.name}</h2>
                </div>
                <p className="text-xs text-zinc-500 mt-1">{meta.tagline}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  {p.id === "vip_monthly" && vipFree ? (
                    <>
                      <span className="font-mono text-4xl font-bold text-emerald-400">0€</span>
                      <span className="font-mono text-lg text-zinc-600 line-through ml-2">€{p.amount}</span>
                      <span className="text-zinc-500">/ {meta.period}</span>
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-4xl font-bold">€{p.amount}</span>
                      <span className="text-zinc-500">/ {meta.period}</span>
                    </>
                  )}
                </div>
                {p.id === "vip_monthly" && vipFree && (
                  <span className={`inline-block mt-2 text-[11px] font-mono px-2 py-0.5 rounded-sm ${acc.chip}`}>GRATIS fino al 31/08/2026</span>
                )}
                <ul className="mt-6 space-y-3 flex-1">
                  {meta.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check size={18} className="text-emerald-400 shrink-0 mt-0.5" /> {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 space-y-2">
                  <a data-testid={`agent-btn-${p.id}`} href={TELEGRAM} target="_blank" rel="noopener noreferrer"
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold transition-colors ${acc.btn}`}>
                    <MessageCircle size={16} /> {p.id === "vip_monthly" && vipFree ? "Scrivici per diventare VIP ora" : "Scrivici su Telegram"}
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {user?.is_premium && !isAdmin && (
        <div className="text-center mt-8">
          <button data-testid="cancel-subscription-btn" onClick={cancel}
            className="text-xs text-zinc-500 hover:text-red-400 transition-colors underline underline-offset-4">
            Annulla abbonamento
          </button>
        </div>
      )}
      <p className="text-center text-xs text-zinc-600 mt-6">Scrivici su Telegram per attivare il tuo abbonamento e scegliere il metodo di pagamento che preferisci.</p>
    </div>
  );
}
